// Client-side wrapper for the `log_audit_event` Supabase RPC.
//
// Call `logAudit({action: 'account_deleted', ...})` at any site that
// performs a sensitive action. The call is fire-and-forget: if the audit
// write fails (network blip, Supabase down, RPC renamed), we log to
// console / Sentry but never break the primary action. The audit trail
// is a *side channel* — the main flow never depends on it.
//
// ADR: docs/adr/0015-audit-log.md
// Migration: supabase/migrations/2026-04-24-audit-log.sql

import { supabase } from './supabase'
import { asJson } from './db-types'
import { createLogger } from './logger'

const log = createLogger('audit')

// ─── Event catalogue ────────────────────────────────────────────────────────
//
// Typed union of supported audit events. Each case names the fields it
// requires. Extending: add a new case here *and* use `logAudit({...})` at
// the site that performs the action. TypeScript enforces that the payload
// matches one of these shapes.
//
// Naming: snake_case, past tense for completed actions, verb_noun when
// the action is the main signal ("invite_revoked" not "revoke_invite").

export type AuditEvent =
  | {
      action: 'account_deleted'
      resourceType: 'profile'
      resourceId: string // the profile id being deleted
    }
  | {
      action: 'team_invite_sent'
      resourceType: 'team'
      resourceId: string // team id
      metadata: { email: string; invitationId: string }
    }
  | {
      action: 'team_invite_accepted'
      resourceType: 'team'
      resourceId: string // team id
      metadata?: { invitationId: string }
    }
  | {
      action: 'team_invite_revoked'
      resourceType: 'team_invitation'
      resourceId: string // invitation id
    }
  | {
      action: 'team_member_removed'
      resourceType: 'team'
      resourceId: string // team id
      metadata: { removedUserId: string }
    }
  | {
      action: 'team_left'
      resourceType: 'team'
      resourceId: string // team id
    }
  | {
      action: 'crm_connected'
      resourceType: 'crm'
      resourceId: 'affinity' | 'attio' | 'hubspot'
    }
  | {
      action: 'crm_disconnected'
      resourceType: 'crm'
      resourceId: 'affinity' | 'attio' | 'hubspot'
    }
  | {
      action: 'data_exported'
      resourceType: 'profile'
      resourceId: 'self' // self-export from Settings; not parameterisable
    }

/**
 * Normalised metadata type — matches the Supabase `jsonb` column shape.
 * Deliberately flat (one level) to keep admin search / export simple.
 */
type AuditMetadata = Record<string, string | number | boolean | null>

// ─── Entry point ────────────────────────────────────────────────────────────

/**
 * Log an audit event. Fire-and-forget — errors are logged but never
 * thrown, so a failed audit never blocks the primary action.
 *
 * Returns a promise that resolves to the new audit row id on success, or
 * `null` on failure (e.g. RPC errored, user not authenticated). Callers
 * who want to reference the id (e.g. "this deletion is recorded as audit
 * row N") can `await` it; most sites should just call and move on.
 *
 *   // Fire and forget — typical usage
 *   void logAudit({ action: 'crm_connected', resourceType: 'crm', resourceId: 'affinity' })
 *
 *   // When the id matters
 *   const auditId = await logAudit({ action: 'account_deleted', ... })
 */
export async function logAudit(event: AuditEvent): Promise<number | null> {
  try {
    const metadata = 'metadata' in event ? (event.metadata as AuditMetadata | undefined) : undefined
    const { data, error } = await supabase.rpc('log_audit_event', {
      p_action: event.action,
      p_resource_type: event.resourceType ?? null,
      p_resource_id: event.resourceId ?? null,
      p_metadata: metadata ? asJson(metadata) : null,
    })
    if (error) {
      log.error(`audit write failed: ${event.action}`, error)
      return null
    }
    // RPC returns bigint → supabase-js serialises as number (within safe
    // integer range for the foreseeable future).
    return typeof data === 'number' ? data : null
  } catch (e) {
    log.error(`audit write threw: ${event.action}`, e)
    return null
  }
}
